/**
 * The Gateway Power platform credit as a Word paragraph: the small G mark
 * (light-background PNG — Word pages are white) followed by the shared credit
 * line. Used by the site-report DOCX (cover-page footer) and the economic
 * contribution brief DOCX (under its title block), so both Word surfaces print
 * the same mark and the same words as the viewer / PDF credit.
 *
 * The image is embedded from bytes (never fetched), so it works identically in
 * the browser exporter and in the server's immutable artifact projector.
 */
import { AlignmentType, ImageRun, Paragraph, TextRun } from 'docx';
import {
  GATEWAY_POWER_NAME,
  REPORT_PLATFORM_CREDIT_LINE,
  gatewayPowerMarkLightPngBytes,
} from './gatewayPowerReportBrand';

export interface DocxPlatformCreditOptions {
  /** Font family name (the document's body font). */
  font?: string;
  /** Hex colour without '#', e.g. '6B7280'. */
  color: string;
  /** Run size in half-points (Word's unit). */
  sizeHalfPoints: number;
  alignment?: (typeof AlignmentType)[keyof typeof AlignmentType];
  spacing?: { before?: number; after?: number };
}

/** The alt text Word shows for the mark (docPr title/descr). */
export const DOCX_PLATFORM_CREDIT_MARK_ALT = {
  name: 'gateway-power-mark',
  title: GATEWAY_POWER_NAME,
  description: `${GATEWAY_POWER_NAME} logo mark`,
} as const;

export function docxPlatformCreditParagraph(options: DocxPlatformCreditOptions): Paragraph {
  // Mark edge = the text size (half-points → pt → px at 96 dpi), so the G sits
  // at cap height and never outweighs the line.
  const markPx = Math.max(8, Math.round((options.sizeHalfPoints / 2) * (96 / 72)));
  return new Paragraph({
    alignment: options.alignment ?? AlignmentType.CENTER,
    ...(options.spacing ? { spacing: options.spacing } : {}),
    children: [
      new ImageRun({
        type: 'png',
        data: gatewayPowerMarkLightPngBytes(),
        transformation: { width: markPx, height: markPx },
        altText: { ...DOCX_PLATFORM_CREDIT_MARK_ALT },
      }),
      new TextRun({
        text: `  ${REPORT_PLATFORM_CREDIT_LINE}`,
        size: options.sizeHalfPoints,
        color: options.color,
        ...(options.font ? { font: options.font } : {}),
      }),
    ],
  });
}
